import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Music,
  Plus,
  Search,
  Filter,
  Heart,
  HeartOff,
  ExternalLink,
  Edit,
  Trash2,
  Youtube,
  FileText,
  Upload,
  Download,
  Globe,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { isSinger, bpmToTempoLabel, stripChords } from '../utils/singerFriendly';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MUSIC_KEYS = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'Db', 'Ab', 'Eb', 'Bb', 'F', 'Am', 'Em', 'Bm', 'F#m', 'C#m', 'G#m', 'D#m', 'Bbm', 'Fm', 'Cm', 'Gm', 'Dm'];
const THEMES = ['Adoración', 'Alabanza', 'Avivamiento', 'Gratitud', 'Fe', 'Esperanza', 'Amor', 'Gracia', 'Salvación', 'Victoria', 'Navidad', 'Pascua'];
const EVENTS = ['Apertura', 'Alabanza', 'Adoración', 'Ofrenda', 'Cierre', 'Especial'];

export const SongsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const singerMode = isSinger(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(searchParams.get('new') === 'true');
  const [showYoutubeDialog, setShowYoutubeDialog] = useState(false);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [favorites, setFavorites] = useState([]);
  
  // Filters
  const [search, setSearch] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [filterTheme, setFilterTheme] = useState('');
  const [filterHymnal, setFilterHymnal] = useState('');
  const [hymnals, setHymnals] = useState([]);
  
  // New song form
  const [newSong, setNewSong] = useState({
    title: '', lyrics: '', original_key: 'G', bpm: '', theme: '', event: '', youtube_url: '', tags: '',
    author_name: '', genre: '', hymnal: '', hymnal_number: ''
  });
  const [saving, setSaving] = useState(false);
  
  // YouTube extraction
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  
  // PDF upload
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Acorde import
  const [showAcordeDialog, setShowAcordeDialog] = useState(false);
  const [acordeStep, setAcordeStep] = useState(1);
  const [acordeLoading, setAcordeLoading] = useState(false);
  const [acordeCategories, setAcordeCategories] = useState([]);
  const [categoryMappings, setCategoryMappings] = useState({});
  const [acordeCatalog, setAcordeCatalog] = useState([]);
  const [acordeSearch, setAcordeSearch] = useState('');
  const [acordeFilterCategory, setAcordeFilterCategory] = useState('');
  const [selectedAcordeSongs, setSelectedAcordeSongs] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    fetchSongs();
    fetchFavorites();
  }, [search, filterKey, filterTheme, filterHymnal]);

  useEffect(() => {
    fetchHymnals();
  }, []);

  const fetchSongs = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterKey) params.append('key', filterKey);
      if (filterTheme) params.append('theme', filterTheme);
      if (filterHymnal) params.append('hymnal', filterHymnal);
      
      const response = await fetch(`${API}/songs?${params}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSongs(data);
      }
    } catch (error) {
      console.error('Error fetching songs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await fetch(`${API}/favorites`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.map(s => s.song_id));
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const fetchHymnals = async () => {
    try {
      const response = await fetch(`${API}/hymnals`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setHymnals(data);
      }
    } catch (error) {
      console.error('Error fetching hymnals:', error);
    }
  };

  const handleCreateSong = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...newSong,
        bpm: newSong.bpm ? parseInt(newSong.bpm) : null,
        hymnal_number: newSong.hymnal_number ? parseInt(newSong.hymnal_number) : null,
        hymnal: newSong.hymnal || null,
        author_name: newSong.author_name || null,
        genre: newSong.genre || null,
        tags: newSong.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      
      const response = await fetch(`${API}/songs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success(singerMode ? 'Canción enviada para revisión' : 'Canción creada exitosamente');
        setShowNewDialog(false);
        setNewSong({ title: '', lyrics: '', original_key: 'G', bpm: '', theme: '', event: '', youtube_url: '', tags: '', author_name: '', genre: '', hymnal: '', hymnal_number: '' });
        fetchSongs();
      } else {
        throw new Error('Error creating song');
      }
    } catch (error) {
      toast.error('Error al crear la canción');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFavorite = async (songId) => {
    const isFavorite = favorites.includes(songId);
    try {
      const response = await fetch(`${API}/favorites/${songId}`, {
        method: isFavorite ? 'DELETE' : 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        if (isFavorite) {
          setFavorites(prev => prev.filter(id => id !== songId));
          toast.success('Eliminado de favoritos');
        } else {
          setFavorites(prev => [...prev, songId]);
          toast.success('Agregado a favoritos');
        }
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const handleExtractYoutube = async () => {
    if (!youtubeUrl) return;
    setExtracting(true);
    try {
      const response = await fetch(`${API}/ai/extract-youtube`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ url: youtubeUrl })
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewSong(prev => ({
          ...prev,
          title: data.title || prev.title,
          lyrics: data.lyrics || prev.lyrics,
          youtube_url: youtubeUrl
        }));
        setShowYoutubeDialog(false);
        setShowNewDialog(true);
        toast.success('Letra extraída exitosamente');
      } else {
        throw new Error('Extraction failed');
      }
    } catch (error) {
      toast.error('Error al extraer letra de YouTube');
    } finally {
      setExtracting(false);
    }
  };

  const handleUploadPdf = async () => {
    if (!pdfFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      
      const response = await fetch(`${API}/ai/process-pdf`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.extracted_songs?.length > 0) {
          toast.success(`Se encontraron ${data.extracted_songs.length} canciones`);
          // Create songs from extracted data
          for (const song of data.extracted_songs) {
            await fetch(`${API}/songs`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
              credentials: 'include',
              body: JSON.stringify({
                title: song.title,
                lyrics: song.lyrics,
                original_key: song.suggested_key || 'G',
                tags: ['himnario']
              })
            });
          }
          fetchSongs();
        } else {
          toast.info('No se pudieron extraer canciones del PDF');
        }
        setShowPdfDialog(false);
      }
    } catch (error) {
      toast.error('Error al procesar PDF');
    } finally {
      setUploading(false);
      setPdfFile(null);
    }
  };

  // ===== Acorde import functions =====
  const DEFAULT_CATEGORY_MAPPINGS = {
    'Adoración': 'Adoración',
    'Alabanza': 'Alabanza',
    'Agradecimiento': 'Gratitud',
    'Renovación': 'Avivamiento',
    'Venida del Señor': 'Esperanza',
    'Levantamiento': 'Victoria',
    'Bautismo': 'Gracia',
    'Misiones': 'Fe',
    'Amor': 'Amor',
    'Salvación': 'Salvación',
    'Navidad': 'Navidad',
    'Pascua': 'Pascua',
  };

  const fetchAcordeCategories = async () => {
    setAcordeLoading(true);
    try {
      const response = await fetch(`${API}/import/acorde/categories`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAcordeCategories(data.categories || []);
        // Initialize mappings with smart defaults
        const mappings = {};
        (data.categories || []).forEach(cat => {
          const name = cat.name || cat.category_name || '';
          mappings[name] = DEFAULT_CATEGORY_MAPPINGS[name] || '';
        });
        setCategoryMappings(mappings);
      } else {
        toast.error('Error al cargar categorías de Acorde');
      }
    } catch (error) {
      toast.error('No se pudo conectar con Acorde');
    } finally {
      setAcordeLoading(false);
    }
  };

  const fetchAcordeCatalog = async () => {
    setAcordeLoading(true);
    try {
      const response = await fetch(`${API}/import/acorde/catalog`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAcordeCatalog(data.songs || []);
      } else {
        toast.error('Error al cargar catálogo de Acorde');
      }
    } catch (error) {
      toast.error('No se pudo conectar con Acorde');
    } finally {
      setAcordeLoading(false);
    }
  };

  const handleAcordeImport = async () => {
    if (selectedAcordeSongs.size === 0) return;
    setImporting(true);
    const allIds = Array.from(selectedAcordeSongs);
    const BATCH_SIZE = 50;
    const batches = [];
    for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
      batches.push(allIds.slice(i, i + BATCH_SIZE));
    }

    const aggregated = { imported: 0, skipped: 0, errors: 0, details: [] };

    try {
      for (let i = 0; i < batches.length; i++) {
        if (batches.length > 1) {
          toast.info(`Importando lote ${i + 1} de ${batches.length}...`);
        }
        const response = await fetch(`${API}/import/acorde`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({
            song_ids: batches[i],
            category_mappings: categoryMappings
          })
        });
        if (response.ok) {
          const result = await response.json();
          aggregated.imported += result.imported;
          aggregated.skipped += result.skipped;
          aggregated.errors += result.errors;
          aggregated.details.push(...result.details);
        } else {
          const errorData = await response.json().catch(() => ({}));
          aggregated.errors += batches[i].length;
          batches[i].forEach(id => aggregated.details.push({
            acorde_id: id, title: '?', status: 'error',
            reason: errorData.detail || `Lote ${i + 1} falló`
          }));
        }
      }

      setImportResult(aggregated);
      setAcordeStep(3);
      toast.success(`Importadas: ${aggregated.imported}, Omitidas: ${aggregated.skipped}, Errores: ${aggregated.errors}`);
      fetchSongs();
    } catch (error) {
      toast.error('Error de conexión al importar canciones');
    } finally {
      setImporting(false);
    }
  };

  const toggleAcordeSong = (acordeId) => {
    setSelectedAcordeSongs(prev => {
      const next = new Set(prev);
      if (next.has(acordeId)) next.delete(acordeId);
      else next.add(acordeId);
      return next;
    });
  };

  const filteredAcordeCatalog = acordeCatalog.filter(song => {
    if (acordeSearch && !song.title.toLowerCase().includes(acordeSearch.toLowerCase()) &&
        !song.artist.toLowerCase().includes(acordeSearch.toLowerCase())) return false;
    if (acordeFilterCategory && song.category_name !== acordeFilterCategory) return false;
    return true;
  });

  const acordeUniqueCategories = [...new Set(acordeCatalog.map(s => s.category_name))].filter(Boolean).sort();

  const toggleAllFiltered = () => {
    const selectable = filteredAcordeCatalog.filter(s => !s.already_exists);
    const allSelected = selectable.every(s => selectedAcordeSongs.has(s.acorde_id));
    setSelectedAcordeSongs(prev => {
      const next = new Set(prev);
      selectable.forEach(s => {
        if (allSelected) next.delete(s.acorde_id);
        else next.add(s.acorde_id);
      });
      return next;
    });
  };

  const openAcordeDialog = () => {
    setShowAcordeDialog(true);
    setAcordeStep(1);
    setSelectedAcordeSongs(new Set());
    setImportResult(null);
    setAcordeSearch('');
    setAcordeFilterCategory('');
    if (acordeCategories.length === 0) fetchAcordeCategories();
  };

  const goToAcordeStep2 = () => {
    setAcordeStep(2);
    if (acordeCatalog.length === 0) fetchAcordeCatalog();
  };

  const getKeyBadgeClass = (key) => {
    const keyMap = {
      'C': 'bg-key-c', 'G': 'bg-key-g', 'D': 'bg-key-d',
      'A': 'bg-key-a', 'E': 'bg-key-e', 'B': 'bg-key-b',
      'F#': 'bg-key-fsharp', 'Db': 'bg-key-db', 'Ab': 'bg-key-ab',
      'Eb': 'bg-key-eb', 'Bb': 'bg-key-bb', 'F': 'bg-key-f'
    };
    const baseKey = key?.replace('m', '');
    return keyMap[baseKey] || 'bg-primary';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="songs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Biblioteca de Canciones</h1>
          <p className="text-muted-foreground">{songs.length} canciones disponibles</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!singerMode && <Dialog open={showYoutubeDialog} onOpenChange={setShowYoutubeDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="youtube-extract-btn">
                <Youtube className="w-4 h-4" />
                YouTube
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Extraer letra de YouTube</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>URL de YouTube</Label>
                  <Input
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    data-testid="youtube-url-input"
                  />
                </div>
                <Button onClick={handleExtractYoutube} disabled={extracting || !youtubeUrl} className="w-full">
                  {extracting ? 'Extrayendo...' : 'Extraer letra'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>}

          {!singerMode && <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="pdf-upload-btn">
                <FileText className="w-4 h-4" />
                PDF
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Cargar himnario PDF</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                  <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setPdfFile(e.target.files?.[0])}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <Label htmlFor="pdf-upload" className="cursor-pointer text-primary hover:underline">
                    Seleccionar archivo PDF
                  </Label>
                  {pdfFile && <p className="mt-2 text-sm text-muted-foreground">{pdfFile.name}</p>}
                </div>
                <Button onClick={handleUploadPdf} disabled={uploading || !pdfFile} className="w-full">
                  {uploading ? 'Procesando...' : 'Procesar PDF'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>}

          {/* Acorde Import Dialog */}
          {!singerMode &&
          <Dialog open={showAcordeDialog} onOpenChange={(open) => {
            if (open) openAcordeDialog();
            else setShowAcordeDialog(false);
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="acorde-import-btn">
                <Globe className="w-4 h-4" />
                Acorde
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>
                  {acordeStep === 1 && 'Paso 1: Mapear categorías'}
                  {acordeStep === 2 && 'Paso 2: Seleccionar canciones'}
                  {acordeStep === 3 && 'Resultado de importación'}
                </DialogTitle>
              </DialogHeader>

              {/* Step 1: Category Mapping */}
              {acordeStep === 1 && (
                <div className="flex flex-col gap-4 pt-2 flex-1 min-h-0">
                  {acordeLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      <span className="ml-3 text-muted-foreground">Cargando categorías de Acorde...</span>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Asigna cada categoría de Acorde a un tema de ChurchMedley. Las canciones se importarán con el tema que elijas.
                      </p>
                      <div className="overflow-y-auto max-h-[50vh] border rounded-lg">
                        <div className="divide-y">
                          {Object.entries(categoryMappings).map(([category, theme]) => (
                            <div key={category} className="flex items-center gap-4 p-3">
                              <span className="font-medium min-w-[180px]">{category}</span>
                              <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                              <Select
                                value={theme || 'none'}
                                onValueChange={(v) => setCategoryMappings(prev => ({
                                  ...prev,
                                  [category]: v === 'none' ? '' : v
                                }))}
                              >
                                <SelectTrigger className="w-[200px]">
                                  <SelectValue placeholder="Sin tema" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Sin tema</SelectItem>
                                  {THEMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              {theme && <Badge variant="secondary">{theme}</Badge>}
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button onClick={goToAcordeStep2} className="w-full gap-2">
                        Continuar <ArrowRight className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* Step 2: Browse & Select Songs */}
              {acordeStep === 2 && (
                <div className="flex flex-col gap-3 pt-2 flex-1 min-h-0">
                  {acordeLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      <span className="ml-3 text-muted-foreground">Cargando catálogo de Acorde...</span>
                    </div>
                  ) : (
                    <>
                      {/* Filters */}
                      <div className="flex gap-3">
                        <div className="flex-1 relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            placeholder="Buscar en Acorde..."
                            value={acordeSearch}
                            onChange={(e) => setAcordeSearch(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                        <Select
                          value={acordeFilterCategory || 'all'}
                          onValueChange={(v) => setAcordeFilterCategory(v === 'all' ? '' : v)}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="Categoría" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todas las categorías</SelectItem>
                            {acordeUniqueCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{filteredAcordeCatalog.length} canciones</span>
                        <div className="flex items-center gap-3">
                          <Button variant="ghost" size="sm" onClick={toggleAllFiltered}>
                            Seleccionar todas ({filteredAcordeCatalog.filter(s => !s.already_exists).length})
                          </Button>
                          <span className="font-medium text-foreground">{selectedAcordeSongs.size} seleccionadas</span>
                        </div>
                      </div>

                      {/* Song list */}
                      <div className="overflow-y-auto max-h-[45vh] border rounded-lg">
                        <div className="divide-y">
                          {filteredAcordeCatalog.map(song => (
                            <div
                              key={song.acorde_id}
                              className={`flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors ${song.already_exists ? 'opacity-50' : ''}`}
                            >
                              <Checkbox
                                checked={selectedAcordeSongs.has(song.acorde_id)}
                                onCheckedChange={() => toggleAcordeSong(song.acorde_id)}
                                disabled={song.already_exists}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{song.title}</p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {song.artist} &middot; {song.category_name} &middot; {song.genre_name}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge variant="outline">{song.mapped_key}</Badge>
                                {categoryMappings[song.category_name] && (
                                  <Badge variant="secondary">{categoryMappings[song.category_name]}</Badge>
                                )}
                                {song.has_chords && (
                                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs">Acordes</Badge>
                                )}
                                {song.already_exists && (
                                  <Badge variant="destructive" className="text-xs">Ya existe</Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-3">
                        <Button variant="outline" onClick={() => setAcordeStep(1)} className="gap-2">
                          <ArrowLeft className="w-4 h-4" /> Volver
                        </Button>
                        <Button
                          onClick={handleAcordeImport}
                          disabled={importing || selectedAcordeSongs.size === 0}
                          className="flex-1 gap-2"
                        >
                          {importing ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Importando {selectedAcordeSongs.size} canciones...</>
                          ) : (
                            <><Download className="w-4 h-4" /> Importar {selectedAcordeSongs.size} canciones</>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 3: Results */}
              {acordeStep === 3 && importResult && (
                <div className="flex flex-col gap-4 pt-2">
                  <div className="flex gap-6 text-sm">
                    <span className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-5 h-5" /> {importResult.imported} importadas
                    </span>
                    <span className="flex items-center gap-2 text-yellow-600">
                      <AlertCircle className="w-5 h-5" /> {importResult.skipped} omitidas
                    </span>
                    <span className="flex items-center gap-2 text-red-600">
                      <XCircle className="w-5 h-5" /> {importResult.errors} errores
                    </span>
                  </div>

                  <div className="overflow-y-auto max-h-[50vh] border rounded-lg">
                    <div className="divide-y">
                      {importResult.details.map((d, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 text-sm">
                          {d.status === 'imported' && <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />}
                          {d.status === 'skipped' && <AlertCircle className="w-4 h-4 text-yellow-600 shrink-0" />}
                          {d.status === 'error' && <XCircle className="w-4 h-4 text-red-600 shrink-0" />}
                          <span className="font-medium truncate flex-1">{d.title}</span>
                          {d.reason && <span className="text-muted-foreground text-xs">{d.reason}</span>}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => {
                      setAcordeStep(1);
                      setSelectedAcordeSongs(new Set());
                      setImportResult(null);
                      setAcordeCatalog([]);
                    }} className="gap-2">
                      Importar más
                    </Button>
                    <Button onClick={() => setShowAcordeDialog(false)} className="flex-1">
                      Cerrar
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>}

          <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="new-song-btn">
                <Plus className="w-4 h-4" />
                Nueva canción
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Agregar nueva canción</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateSong} className="space-y-4 pt-4">
                {singerMode && (
                  <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-200">
                    Tu canción será enviada para revisión. Un músico la aprobará y completará los datos musicales.
                  </div>
                )}
                <div className={`grid ${singerMode ? '' : 'md:grid-cols-2'} gap-4`}>
                  <div className="space-y-2">
                    <Label>Título *</Label>
                    <Input
                      value={newSong.title}
                      onChange={(e) => setNewSong({ ...newSong, title: e.target.value })}
                      required
                      data-testid="song-title-input"
                    />
                  </div>
                  {!singerMode && (
                    <div className="space-y-2">
                      <Label>Tonalidad *</Label>
                      <Select value={newSong.original_key} onValueChange={(v) => setNewSong({ ...newSong, original_key: v })}>
                        <SelectTrigger data-testid="song-key-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MUSIC_KEYS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Letra *</Label>
                  <Textarea
                    value={newSong.lyrics}
                    onChange={(e) => setNewSong({ ...newSong, lyrics: e.target.value })}
                    rows={8}
                    required
                    placeholder="Verso 1:&#10;..."
                    data-testid="song-lyrics-input"
                  />
                </div>

                <div className={`grid ${singerMode ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
                  {!singerMode && (
                    <div className="space-y-2">
                      <Label>BPM</Label>
                      <Input
                        type="number"
                        value={newSong.bpm}
                        onChange={(e) => setNewSong({ ...newSong, bpm: e.target.value })}
                        placeholder="120"
                        data-testid="song-bpm-input"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Tema</Label>
                    <Select value={newSong.theme} onValueChange={(v) => setNewSong({ ...newSong, theme: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {THEMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Evento</Label>
                    <Select value={newSong.event} onValueChange={(v) => setNewSong({ ...newSong, event: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENTS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Autor (opcional)</Label>
                    <Input
                      value={newSong.author_name}
                      onChange={(e) => setNewSong({ ...newSong, author_name: e.target.value })}
                      placeholder="Nombre del autor"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Género (opcional)</Label>
                    <Input
                      value={newSong.genre}
                      onChange={(e) => setNewSong({ ...newSong, genre: e.target.value })}
                      placeholder="Balada, Pop, Himno..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Himnario (opcional)</Label>
                    <Select value={newSong.hymnal || '_none'} onValueChange={(v) => setNewSong({ ...newSong, hymnal: v === '_none' ? '' : v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar himnario" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Ninguno</SelectItem>
                        <SelectItem value="Manantial de Inspiración">Manantial de Inspiración</SelectItem>
                        <SelectItem value="Lluvia de Bendición">Lluvia de Bendición</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label># Himno (opcional)</Label>
                    <Input
                      type="number"
                      value={newSong.hymnal_number}
                      onChange={(e) => setNewSong({ ...newSong, hymnal_number: e.target.value })}
                      placeholder="123"
                    />
                  </div>
                </div>

                {!singerMode && (
                  <>
                    <div className="space-y-2">
                      <Label>URL de YouTube (opcional)</Label>
                      <Input
                        value={newSong.youtube_url}
                        onChange={(e) => setNewSong({ ...newSong, youtube_url: e.target.value })}
                        placeholder="https://www.youtube.com/watch?v=..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Etiquetas (separadas por coma)</Label>
                      <Input
                        value={newSong.tags}
                        onChange={(e) => setNewSong({ ...newSong, tags: e.target.value })}
                        placeholder="himno, clásico, coral"
                      />
                    </div>
                  </>
                )}
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="save-song-btn">
                    {saving ? 'Guardando...' : 'Guardar canción'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por título, letra, autor o #número..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            {!singerMode && (
              <Select value={filterKey || 'all'} onValueChange={(v) => setFilterKey(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-full md:w-40" data-testid="filter-key">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Tonalidad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {MUSIC_KEYS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={filterTheme || 'all'} onValueChange={(v) => setFilterTheme(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full md:w-40" data-testid="filter-theme">
                <SelectValue placeholder="Tema" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {THEMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            {hymnals.length > 0 && (
              <Select value={filterHymnal || 'all'} onValueChange={(v) => setFilterHymnal(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-hymnal">
                  <SelectValue placeholder="Himnario" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los himnarios</SelectItem>
                  {hymnals.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Songs Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-48 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : songs.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {songs.map((song, index) => (
            <Card
              key={song.song_id}
              className="border-border/50 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`song-card-${song.song_id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  {!singerMode ? (
                    <div className={`key-badge ${getKeyBadgeClass(song.original_key)}`}>
                      {song.original_key}
                    </div>
                  ) : song.status === 'pending' ? (
                    <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pendiente</Badge>
                  ) : song.status === 'rejected' ? (
                    <Badge variant="destructive">Rechazada</Badge>
                  ) : song.theme ? (
                    <Badge variant="secondary">{song.theme}</Badge>
                  ) : (
                    <div />
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleToggleFavorite(song.song_id)}
                    data-testid={`favorite-btn-${song.song_id}`}
                  >
                    {favorites.includes(song.song_id) ? (
                      <Heart className="w-4 h-4 text-destructive fill-destructive" />
                    ) : (
                      <Heart className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
                <Link to={`/songs/${song.song_id}`}>
                  <h3 className="font-heading font-semibold text-lg mb-2 hover:text-primary transition-colors line-clamp-1">
                    {song.hymnal_number ? `#${song.hymnal_number} ` : ''}{song.title}
                  </h3>
                </Link>

                {song.author_name && (
                  <p className="text-xs text-muted-foreground truncate mb-1">{song.author_name}</p>
                )}

                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {singerMode ? stripChords(song.lyrics?.split('\n')[0]) : song.lyrics?.split('\n')[0]}
                </p>

                <div className="flex items-center gap-2 flex-wrap">
                  {!singerMode && song.theme && <Badge variant="secondary">{song.theme}</Badge>}
                  {singerMode && song.bpm && <Badge variant="outline">{bpmToTempoLabel(song.bpm)}</Badge>}
                  {!singerMode && song.bpm && <Badge variant="outline">{song.bpm} BPM</Badge>}
                  {singerMode && song.event && <Badge variant="outline">{song.event}</Badge>}
                  {song.genre && <Badge variant="outline">{song.genre}</Badge>}
                  {song.hymnal && (
                    <Badge variant="outline" className="text-xs">
                      {song.hymnal}
                    </Badge>
                  )}
                  {song.youtube_url && (
                    <a href={song.youtube_url} target="_blank" rel="noopener noreferrer">
                      <Badge variant="outline" className="gap-1 cursor-pointer hover:bg-muted">
                        <Youtube className="w-3 h-3" />
                        Video
                      </Badge>
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Music className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-xl font-medium mb-2">No se encontraron canciones</h3>
          <p className="text-muted-foreground mb-4">
            {search || filterKey || filterTheme ? 'Intenta ajustar los filtros' : 'Agrega tu primera canción'}
          </p>
          <Button onClick={() => setShowNewDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Agregar canción
          </Button>
        </div>
      )}
    </div>
  );
};

export default SongsPage;
